<template>
  <div class="client-search">
    <Dropdown
      v-model="searchCategory"
      :options="searchCategories"
      option-label="label"
      option-disabled="disabled"
      placeholder="Select"
      class="mr-2 rounded-left"
      panel-class="search-dropdown-panel" />
    <span
      class="p-input-icon-right search-text-field"
      :class="{ disable: !searchCategory }">
      <i class="pi pi-search search-icon" @click="performSearch" />
      <InputText
        v-model="searchVal"
        type="text"
        autocomplete="off"
        placeholder="Search"
        class="rounded-right w-full client-search-input" 
        @input="handleAccountSearch(searchVal)"/>
      <div v-if="showOverlay" class="search-result-overlay">
        <div v-if="loading" class="text-center">
          <img class="spinner-icon p-2" src="@/assets/ajax-loader.gif" />
        </div>
        <ul v-else-if="investmentAccountList.length > 0">
          <li
            v-for="account in investmentAccountList"
            :key="account.accountId"
            @click="goToAccountDetail(account)">
            <label class="result-client-name">
              <i class="pi pi-search" />
              {{ account.clientName }}
            </label>
            <span v-if="account.accountId" class="result-client-id"
              >Account ID {{ account.accountId }}</span
            >
            <span v-if="account.policyNumber" class="result-client-id"
              >Policy ID {{ account.policyNumber }}</span
            >
          </li>
        </ul>
        <p v-else class="no-search-result">
          No results found for <br />
          “{{searchVal}}”
        </p>
      </div>
    </span>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed } from 'vue';
import InputText from 'primevue/inputtext';
import Dropdown from 'primevue/dropdown';
import { useRole } from '@/composables/useRole';
import { AppRoles } from '@/enums/ApplicationClaims.enum';
import { CategoryType } from '@/interfaces/CategoryType';
import { useFetchData } from '@/composables/useFetchData';
import AdviserHubClient from '@/rest-client/AdviserHubClient';
import AdviserHubInvestmentClient from '@/rest-client/AdviserHubInvestmentClient';
import { InvestmentClients } from '@/interfaces/clients/InvestmentClients';
import { debounce } from 'lodash';
import { mapState } from 'pinia';
import { userStore } from '@/stores/UserStore';
import { PostApplicationListRequest } from '@/interfaces/clients/PostApplicationListRequest';

export default defineComponent({
  name: 'ClientSearch',
  components: {
    InputText,
    Dropdown,
  },

  setup() {
    const { hasRole } = useRole();

    const canViewInvestments = computed(() =>
      hasRole(AppRoles.InvestmentAdviser)
    );
    const canViewInsurance = computed(() => hasRole(AppRoles.InsuranceAdviser));

    return {
      canViewInvestments,
      canViewInsurance,
    };
  },

  data() {
    const { data, loading, fetchData } = useFetchData<InvestmentClients>();

    return {
      investmentAccountList: data,
      loading,
      fetchData,
      searchVal: '',
      searchCategory: null as CategoryType | null,
      showOverlay: false,
      debouncedSearch: null as null | Function,
    };
  },

  computed: {
    ...mapState(userStore, ['userInfo']),
    adviserId(): number {
      return this.userInfo.adviserId;
    },
    searchCategories(): CategoryType[] {
      const categories: CategoryType[] = [];
      if (this.canViewInvestments) {
        categories.push({
          label: 'Investments',
          value: 'investments',
          disabled: false,
        });
      }
      if (this.canViewInsurance) {
        categories.push({
          label: 'Insurance',
          value: 'insurance',
          disabled: false,
        });
      }
      return categories;
    },
  },

  watch: {
    searchVal(val: string) {
      if (this.debouncedSearch) {
        this.debouncedSearch(val);
      }
    },
    searchCategories: {
      immediate: true,
      handler(newCategories: CategoryType[]) {
        if (!this.searchCategory && newCategories.length > 0) {
          this.searchCategory =
            newCategories.find((cat) => !cat.disabled) || newCategories[0];
        }
      },
    },
  },

  created() {
    this.debouncedSearch = debounce(this.handleSearch, 350);
  },

  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },

  methods: {
    handleSearch(val: string) {
      const trimmed = val.trim();
      const isNumericOnly = /^\d+$/.test(trimmed);
      const searchByName = !isNumericOnly && trimmed.length >= 2;
      this.showOverlay = trimmed != '' ? true : false;

      if (this.searchCategory?.value === 'investments') {
        let searchId = trimmed;
        if (isNumericOnly) {
          if (trimmed.length < 7) {
            searchId = trimmed.padStart(7, '0');
          } else if (trimmed.length > 7) {
            // Remove only the extra leading zeros so the length matches the input
            const extraLength = trimmed.length - 7;
            const regex = new RegExp(`^0{1,${extraLength}}`);
            searchId = trimmed.replace(regex, '');
          }
        }
        const searchById = isNumericOnly && searchId.length >= 7;
        if (searchById) {
          this.getInvestmentClients('Id', searchId);
        } else if (searchByName) {
          this.getInvestmentClients('Name', trimmed);
        }
      }
      if (this.searchCategory?.value === 'insurance') {
        const searchById = isNumericOnly && trimmed.length >= 5;

        if (searchById) {
          this.getPoliciesList('Id', trimmed);
        } else if (searchByName) {
          this.getPoliciesList('Name', trimmed);
        }
      }
    },

    handleAccountSearch(value: any) {
      this.searchVal = value.trim();
    },

    async getInvestmentClients(key: string, value: string) {
      const params: Record<string, string> = {
        pageSize: '5',
        pageNumber: '1',
        sort: 'ClientName|ASC',
      };

      if (key === 'Id') {
        const trimmedValue = value.trim();

        const isNumeric = /^\d+$/.test(trimmedValue);

        let processedValue = trimmedValue;

        if (isNumeric) {
          if (trimmedValue.length < 7) {
            processedValue = trimmedValue.padStart(7, '0');
          } else if (trimmedValue.length > 7) {
            const extraLength = trimmedValue.length - 7;
            const regex = new RegExp(`^0{1,${extraLength}}`);
            processedValue = trimmedValue.replace(regex, '');
          }
        }
      
        params['filter:ClientId'] = processedValue;
      } else if (key === 'Name') {
        params['filter:ClientName'] = value.toLowerCase();
      }

      this.fetchData(
        async () => {
          const response =
            await AdviserHubInvestmentClient.getInvestmentClients(params);

          this.showOverlay = true;
          return {
            statusCode: response.statusCode,
            body: response.body?.results ?? [],
          };
        },
        (items: any[]): InvestmentClients[] => {
          return items.map((item: any) => ({
            accountId: item.id,
            clientName: item.name?.trim() || '-',
            invAccountId: item.adviser?.id,
          }));
        }
      );
    },

    async getPoliciesList(type?: 'Id' | 'Name', value?: string) {
      const filters = [];

      if (type === 'Id' && value) {
        filters.push({
          propertyName: 'policynumber',
          filterOperator: 'contains',
          value,
        });
      } else if (type === 'Name' && value) {
        filters.push({
          propertyName: 'firstname',
          filterOperator: 'contains',
          value,
        });
      }

      const request = {
        adviserIds: [this.adviserId],
        skip: 0,
        take: 5,
        sorters: [{ column: 'firstName', direction: 'ASC' }],
        filters,
      } as PostApplicationListRequest;

      this.fetchData(
        async () => {
          const response = await AdviserHubClient.getInsurancePoliciesList(
            request
          );

          this.showOverlay = true;
          return {
            statusCode: response.statusCode,
            body: response.body?.policyList ?? [],
          };
        },
        (items: any[]): InvestmentClients[] => {
          return items.map((item: any) => ({
            policyNumber: item.policyNumber,
            policyId: item.policyId,
            clientName: `${item.firstName} ${item.lastName}` || '-',
          }));
        }
      );
    },

    performSearch() {
      if (!this.searchVal || !this.searchCategory) return;

      this.$router.push({
        name: 'clients',
        query: {
          search: this.searchVal,
          category: this.searchCategory.value,
        },
      });
      this.showOverlay = false;
      this.searchVal = '';
    },

    goToAccountDetail(data: any) {
      this.showOverlay = false;

      switch (this.searchCategory?.value) {
        case 'investments':
          this.handleInvestmentNavigation(data);
          break;

        case 'insurance':
          this.handleInsuranceNavigation(data);
          break;
      }

      this.searchVal = '';
    },

    handleInvestmentNavigation(data: any) {
      const targetRoute = {
        name: 'client-account-details',
        params: { id: data.accountId },
      };

      const isOnAccountDetail = this.$route.name === 'client-account-details';
      const isSameAccount = this.$route.params.id === String(data.accountId);

      if (isOnAccountDetail && !isSameAccount) {
        this.$router.push(targetRoute).then(() => this.reloadCurrentRoute());
      } else if (!isOnAccountDetail) {
        this.$router.push(targetRoute);
      }
    },

    handleInsuranceNavigation(data: any) {
      this.$router.push({
        name: 'policies-details',
        params: { id: data.policyId },
        state: { policyNumber: data.policyNumber },
      });
    },

    reloadCurrentRoute() {
      this.$router.go(0);
    },

    handleClickOutside(event: MouseEvent) {
      const container = this.$el.querySelector('.search-text-field');
      if (container && !container.contains(event.target as Node)) {
        this.showOverlay = false;
      }
    },
  },
});
</script>

<style lang="scss" scoped>
@import '@/scss/booster-styles.scss';

.client-search {
  display: flex;
  flex-direction: row;
  justify-content: end;
  align-items: flex-end;
  text-align: right;
  vertical-align: middle;
  height: 100%;
  :deep(.p-dropdown) {
    border-radius: 0.438rem 0px 0px 0.438rem;
    border: 0.063rem solid $booster-medium-gray;
    background-color: $booster-light-gray;
    width: 6.875rem;
    height: 2.125rem;
    flex-shrink: 0;
    display: inline-flex;
    &.p-overlay-open {
      border-bottom-left-radius: 0;
    }
    &:focus {
      outline: none !important;
      box-shadow: none !important;
      border-color: #ccc !important;
    }
    &.p-dropdown-focus {
      outline: none !important;
      box-shadow: none !important;
      border-color: #ccc !important;
    }
    .p-dropdown-label {
      color: $booster-text !important;
      font-family: 'Nunito Sans';
      font-size: 0.875rem;
      font-style: normal;
      font-weight: 400;
      line-height: normal;
      text-overflow: inherit;
      overflow: visible;
    }
    .p-dropdown-trigger {
      justify-content: flex-end;
      padding-right: 0.313rem;
    }
    .p-icon {
      height: 0.75rem;
    }
  }
  .client-search-input {
    border-radius: 0px 0.438rem 0.438rem 0px;
    border: 0.063rem solid $booster-medium-gray;
    background: $booster-background-gray;
    width: 13.813rem;
    height: 2.125rem;
    flex-shrink: 0;
    color: $booster-text;
    font-family: 'Nunito Sans';
    font-size: 0.875rem;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    &::placeholder {
      color: $booster-dark-blue;
    }
    &:focus {
      outline: none;
    }
  }
  .search-text-field {
    position: relative;
    display: inline-flex;
    &.disable {
      pointer-events: none;
    }
    .search-icon {
      position: absolute;
      top: 0.625rem;
      right: 0.625rem;
      font-size: 0.875rem;
      z-index: 9;
      cursor: pointer;
      color: $booster-blue;
    }
  }
}
:deep(.p-dropdown:focus-within),
:deep(.p-dropdown-focus),
:deep(.p-inputtext:focus) {
  outline: none !important;
  box-shadow: none !important;
}
.search-result-overlay {
  position: absolute;
  border-radius: 0.438rem;
  border: 0.063rem solid $booster-medium-gray;
  background: #fff;
  box-shadow: 0 0.625rem 0.625rem 0 rgba(0, 0, 0, 0.05);
  top: 100%;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  width: 100%;
  z-index: 999;
  max-height: 210px;
  overflow: auto;
  .no-search-result {
    margin: 0;
    padding: 1rem;
    color: $booster-breadcrumb-gray;
    font-family: 'Nunito Sans';
    font-size: 0.688rem;
    font-style: normal;
    font-weight: 400;
    line-height: 0.875rem;
    text-align: left;
  }
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    li {
      text-align: left;
      border-bottom: 1px solid $booster-medium-gray;
      padding: 0.625rem;
      cursor: pointer;
      .result-client-name {
        display: block;
        color: $booster-dark-blue;
        font-family: 'Nunito Sans';
        font-size: 0.875rem;
        font-style: normal;
        font-weight: 400;
        line-height: 0.875rem;
        margin-bottom: 0.313rem;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        cursor: pointer;
        .pi-search {
          font-size: 0.75rem;
          color: $booster-breadcrumb-gray;
          line-height: 0.75rem;
        }
      }
      .result-client-id {
        display: block;
        color: $booster-breadcrumb-gray;
        font-family: 'Nunito Sans';
        font-size: 0.688rem;
        font-style: normal;
        font-weight: 400;
        line-height: 0.688rem;
        padding-left: 1rem;
        margin-bottom: 0.188rem;
      }
    }
  }
}
.spinner-icon {
  width: 36px;
  height: 36px;
}
</style>
